import { useNavigate } from 'react-router-dom';
import { Button } from '@ai-tutor/ui';

interface EmptyStateAction {
  label: string;
  path?: string;
  onClick?: () => void;
  variant?: 'default' | 'outline' | 'ghost';
}

interface EmptyStatePromptProps {
  title: string;
  description: string;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  className?: string;
}

/**
 * EmptyStatePrompt - the page-level empty state: a headline, a line of
 * explanation, and up to two calls to action that route somewhere.
 *
 * Distinct from `EmptyState` in `@ai-tutor/ui`, which is the presentational
 * primitive (icon + title + description, no navigation). Reach for that one
 * inside a panel or card; reach for this one when an entire page has nothing
 * to show and the user needs somewhere to go next.
 */
export function EmptyStatePrompt({
  title,
  description,
  primaryAction,
  secondaryAction,
  className = '',
}: EmptyStatePromptProps) {
  const navigate = useNavigate();

  const handleAction = (action?: EmptyStateAction) => {
    if (!action) return;
    if (action.onClick) {
      action.onClick();
    } else if (action.path) {
      navigate(action.path);
    }
  };

  return (
    <div className={`animate-fade-in px-4 py-16 text-center ${className}`}>
      <h2 className='mb-3 text-xl font-semibold text-foreground'>{title}</h2>

      <p className='mx-auto mb-8 max-w-xl text-sm text-muted-foreground'>
        {description}
      </p>

      <div className='flex flex-col items-center justify-center gap-3 sm:flex-row max-sm:[&>button]:h-11'>
        {/* `Button`, not two hand-rolled `<button>`s. The pair here restated
            the default and outline variants in raw utilities, which meant they
            missed every refinement the primitive has had since — the focus
            ring, the elevation tokens that actually render in dark, the
            `motion-safe` press. `variant` comes from the action so a caller can
            still pick, defaulting to the emphasis its slot implies. */}
        {primaryAction && (
          <Button
            variant={primaryAction.variant ?? 'default'}
            onClick={() => handleAction(primaryAction)}
          >
            {primaryAction.label}
          </Button>
        )}

        {secondaryAction && (
          <Button
            variant={secondaryAction.variant ?? 'outline'}
            onClick={() => handleAction(secondaryAction)}
          >
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
}
